// App chrome: toasts, sheets, the logo, the phone tab bar and the desktop nav.
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode, type RefObject } from 'react'
import { Link, NavLink, useLocation } from 'react-router'
import type { Me } from '@wbwl/contract'
import { useMyChallenges } from '../lib/data.ts'
import { countdown } from '../lib/game.ts'
import { dayOf, needsYou } from '../lib/rivals.ts'
import { primeSound, setSoundOn, soundOn } from '../lib/sound.ts'
import { coverPage } from './badge-motion.ts'
import { Icon, type IconName } from './Icon.tsx'

// ---------- toasts ----------

interface ToastItem { id: number; content: ReactNode }
const ToastContext = createContext<(content: ReactNode, ms?: number) => void>(() => {})

export function ToastProvider({ children }: { children: ReactNode }) {
  const [item, setItem] = useState<ToastItem | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const show = useCallback((content: ReactNode, ms = 1800) => {
    clearTimeout(timer.current)
    setItem({ id: Date.now(), content })
    timer.current = setTimeout(() => setItem(null), ms)
  }, [])
  return (
    <ToastContext.Provider value={show}>
      {children}
      <div className="toast-host" role="status" aria-live="polite">
        {item && <div key={item.id} className="toast in">{item.content}</div>}
      </div>
    </ToastContext.Provider>
  )
}

export const useToast = () => useContext(ToastContext)

// ---------- sheet ----------

/** `className`: "pinned" keeps the top in place while a .sheet-scroll inside it scrolls (the achievement lists). */
export function Sheet({ open, onClose, children, label, className }: { open: boolean; onClose(): void; children: ReactNode; label: string; className?: string }) {
  const sheet = useRef<HTMLDivElement>(null)
  const veil = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])
  useDragToClose(sheet, veil, onClose, open)
  // The page under it holds its badges still.
  useEffect(() => (open ? coverPage() : undefined), [open])
  if (!open) return null
  return (
    <div className="sheet-layer" role="dialog" aria-modal="true" aria-label={label}>
      <button ref={veil} type="button" className="veil" aria-label="Close" onClick={onClose} />
      <div ref={sheet} className={className ? `sheet ${className}` : 'sheet'}>
        <div className="grab-zone"><div className="grabber" /></div>
        {children}
      </div>
    </div>
  )
}

/**
 * A bottom sheet you can pull down to close, by its handle, or by the sheet itself when it's
 * scrolled to the top (or the list touched is, in a sheet whose list scrolls: .sheet-scroll). It follows the finger (the veil behind fading with it); let go far enough or
 * fast enough and it closes, otherwise it springs back. Touch only, and only while the handle shows:
 * on a desktop a sheet is a centred dialog without one.
 */
export function useDragToClose(sheet: RefObject<HTMLElement | null>, veil: RefObject<HTMLElement | null>, onClose: () => void, active = true) {
  const close = useRef(onClose)
  close.current = onClose
  useEffect(() => {
    const el = sheet.current
    const handle = el?.querySelector<HTMLElement>('.grabber')
    if (!active || !el || !handle || getComputedStyle(handle).visibility === 'hidden') return
    let startY = 0, startT = 0, dy = 0, dragging = false, byHandle = false
    // What a pull down would scroll: a list inside the sheet that scrolls on its own, or the sheet.
    let scroller: HTMLElement = el
    let timer: ReturnType<typeof setTimeout> | undefined
    const shade = (opacity: string, transition = '') => {
      if (!veil.current) return
      veil.current.style.transition = transition
      veil.current.style.opacity = opacity
    }
    const onStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) return
      byHandle = !!(e.target as Element).closest('.grab-zone')
      scroller = (e.target as Element).closest<HTMLElement>('.sheet-scroll') ?? el
      if (!byHandle && scroller.scrollTop > 0) return
      clearTimeout(timer)
      startY = e.touches[0].clientY
      startT = e.timeStamp
      dy = 0
      dragging = true
      el.style.transition = 'none'
    }
    const onMove = (e: TouchEvent) => {
      if (!dragging) return
      const d = e.touches[0].clientY - startY
      // On the sheet itself, a pull up (or once it's scrolled) is scrolling, not closing.
      if (!byHandle && dy === 0 && (d < 0 || scroller.scrollTop > 0)) { dragging = false; el.style.transition = ''; return }
      dy = Math.max(0, d)
      if (dy === 0) return
      e.preventDefault()
      el.style.translate = `0 ${dy}px`
      shade(String(Math.max(0.15, 1 - dy / el.offsetHeight)))
    }
    const onEnd = (e: TouchEvent) => {
      if (!dragging) return
      dragging = false
      const speed = dy / Math.max(1, e.timeStamp - startT)
      el.style.transition = 'translate .2s ease-out'
      if (dy > Math.min(140, el.offsetHeight * 0.3) || (dy > 24 && speed > 0.5)) {
        el.style.translate = `0 ${el.offsetHeight + 40}px`
        shade('0', 'opacity .2s ease-out')
        timer = setTimeout(() => close.current(), 180)
      } else {
        el.style.translate = ''
        shade('', 'opacity .2s ease-out')
        timer = setTimeout(() => { el.style.transition = ''; if (veil.current) veil.current.style.transition = '' }, 220)
      }
    }
    el.addEventListener('touchstart', onStart, { passive: true })
    el.addEventListener('touchmove', onMove, { passive: false })
    el.addEventListener('touchend', onEnd)
    el.addEventListener('touchcancel', onEnd)
    return () => {
      clearTimeout(timer)
      el.removeEventListener('touchstart', onStart)
      el.removeEventListener('touchmove', onMove)
      el.removeEventListener('touchend', onEnd)
      el.removeEventListener('touchcancel', onEnd)
    }
  }, [sheet, veil, active])
}

// ---------- logo ----------

/** The Letters on Fire wordmark (logo A), swapping its light and dark versions with the theme. */
export function Logo({ height = 26 }: { height?: number }) {
  return (
    <span className="logo" style={{ height }}>
      <img className="logo-paper" src="/brand/logo-small-on-light.svg" alt="Letters on Fire" style={{ height }} />
      <img className="logo-night" src="/brand/logo-small-on-dark.svg" alt="" aria-hidden="true" style={{ height }} />
    </span>
  )
}

// ---------- navigation ----------

const TABS: { to: string; label: string; icon: IconName; end?: boolean }[] = [
  { to: '/', label: 'Today', icon: 'paper', end: true },
  { to: '/leaderboard', label: 'Leaderboard', icon: 'trophy' },
  { to: '/rivals', label: 'Rivals', icon: 'swords' },
  { to: '/you', label: 'You', icon: 'user' },
]

/** Something on Rivals waits for you: an invite, a challenge you haven't finished, a result you haven't seen. */
function useRivalsDot(): boolean {
  const mine = useMyChallenges(false).data
  return !!mine?.editions.some((g) => needsYou(dayOf(g)))
}

export function TabBar() {
  const dot = useRivalsDot()
  return (
    <nav className="tabbar" aria-label="Main">
      {TABS.map((t) => (
        <NavLink key={t.to} to={t.to} end={t.end} className={({ isActive }) => `tab${isActive ? ' on' : ''}`}>
          <Icon name={t.icon} size={24} />{t.label}
          {t.to === '/rivals' && dot && <i className="dot"><span className="sr-only">, something new</span></i>}
        </NavLink>
      ))}
    </nav>
  )
}

/** The desktop header. While you play, sound sits with how-to-play and the theme on the right. */
export function DesktopNav({ me, onHelp }: { me: Me | undefined; onHelp(): void }) {
  const { pathname } = useLocation()
  const playing = pathname.startsWith('/play') || pathname.startsWith('/slow-burn')
  const dot = useRivalsDot()
  return (
    <header className="dnav">
      <Link to="/" className="dnav-brand" aria-label="Letters on Fire home"><Logo height={28} /></Link>
      <nav className="dnav-links" aria-label="Main">
        <NavLink to="/" end className={({ isActive }) => (isActive ? 'on' : '')}>Today</NavLink>
        <NavLink to="/slow-burn" className={({ isActive }) => (isActive ? 'on' : '')}>Slow Burn</NavLink>
        <NavLink to="/leaderboard" className={({ isActive }) => (isActive ? 'on' : '')}>Leaderboard</NavLink>
        <NavLink to="/rivals" className={({ isActive }) => (isActive ? 'on' : '')}>Rivals{dot && <i className="dot"><span className="sr-only">, something new</span></i>}</NavLink>
        <NavLink to="/you" className={({ isActive }) => (isActive ? 'on' : '')}>Archive &amp; stats</NavLink>
      </nav>
      <div className="dnav-right">
        {me && me.streak > 0 && <span className="chip"><Icon name="streak" />{me.streak}-day streak</span>}
        {playing && <SoundButton />}
        <button type="button" className="ibtn" aria-label="How to play" onClick={onHelp}><Icon name="help" /></button>
        <ThemeButton />
        <Link to="/you" className="dnav-av" aria-label="You"><Avatar id={me?.id} name={me?.displayName} src={me?.avatar} size={34} /></Link>
      </div>
    </header>
  )
}

// ---------- theme ----------

export type ThemeChoice = 'auto' | 'paper' | 'night'

export function applyTheme(choice: ThemeChoice) {
  const t = choice === 'auto' ? (matchMedia('(prefers-color-scheme: dark)').matches ? 'night' : 'paper') : choice
  const root = document.documentElement
  root.dataset.theme = t
  // The browser's bars too, as index.html first set them: theme-color, and the manifest Firefox
  // reads an installed app's bar colors from (it picks up the new one when the app next opens).
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', getComputedStyle(root).getPropertyValue('--bg').trim())
  document.querySelector('link[rel="manifest"]')?.setAttribute('href', t === 'night' ? '/site-night.webmanifest' : '/site.webmanifest')
  try { localStorage.setItem('lof:theme', choice) } catch { /* ignore */ }
}

export function currentTheme(): ThemeChoice {
  try { return (localStorage.getItem('lof:theme') as ThemeChoice) || 'auto' } catch { return 'auto' }
}

export function ThemeButton() {
  const [night, setNight] = useState(() => document.documentElement.dataset.theme === 'night')
  return (
    <button type="button" className="ibtn" aria-label={night ? 'Switch to the paper theme' : 'Switch to the night theme'} onClick={() => { applyTheme(night ? 'paper' : 'night'); setNight(!night) }}>
      <Icon name={night ? 'sun' : 'moon'} />
    </button>
  )
}

/** Sound on or off, for the game's ticks, clacks and phoenix. */
export function SoundButton() {
  const [on, setOn] = useState(soundOn)
  return (
    <button type="button" className="ibtn" aria-label={on ? 'Turn sound off' : 'Turn sound on'} onClick={() => { setSoundOn(!on); setOn(!on); if (!on) primeSound() }}>
      <Icon name={on ? 'sound' : 'muted'} />
    </button>
  )
}

// ---------- bits ----------

const AVATAR_COLORS = ['#F2BE3A', '#C9D8F2', '#F2C6B8', '#CDE3C4', '#E3D3EE', '#F6DFA6', '#BFE0D8', '#BFD7C9', '#D9D5CC']
export function avatarColor(id: string | undefined): string {
  if (!id) return AVATAR_COLORS[7]
  let h = 0
  for (const c of id) h = (h * 31 + c.charCodeAt(0)) >>> 0
  return AVATAR_COLORS[h % AVATAR_COLORS.length]
}

export function initials(name: string | undefined): string {
  if (!name) return ''
  const parts = name.trim().split(/\s+/)
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? parts[0]?.[1] ?? '')).toUpperCase()
}

/** A player's photo, or their initials on their colour when they have none (or it won't load). */
export function Avatar({ id, name, size = 34, src }: { id?: string; name?: string; size?: number; src?: string | null }) {
  const [broken, setBroken] = useState<string | null>(null)
  if (src && broken !== src) {
    return <img className="av" src={src} alt="" aria-hidden="true" width={size} height={size} style={{ width: size, height: size }} loading="lazy" decoding="async" onError={() => setBroken(src)} />
  }
  return <span className="av" style={{ width: size, height: size, fontSize: size * 0.38, background: avatarColor(id) }} aria-hidden="true">{initials(name)}</span>
}

/** Re-renders every second with the time left until `to`. */
export function Countdown({ to }: { to: string }) {
  const [, tick] = useState(0)
  useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 1000)
    return () => clearInterval(t)
  }, [])
  return <>{countdown(to)}</>
}

export function Loading({ label = 'Printing today’s edition…' }: { label?: string }) {
  return <div className="loading"><div className="skel-board">{Array.from({ length: 30 }, (_, i) => <i key={i} className="skel" />)}</div><div className="faint mono">{label}</div></div>
}

export function NotFound() {
  return (
    <div className="page not-found">
      <h1 className="h-serif">Stop the presses.</h1>
      <p className="muted">That page doesn’t exist.</p>
      <Link className="btn ink" to="/">Back to today</Link>
    </div>
  )
}

export function ErrorNote({ error, retry }: { error: unknown; retry?(): void }) {
  const offline = typeof navigator !== 'undefined' && !navigator.onLine
  return (
    <div className="error-note">
      <Icon name={offline ? 'wifioff' : 'info'} size={22} />
      <div>
        <b>{offline ? 'You’re offline.' : 'Something went wrong.'}</b>
        <div className="muted">{offline ? 'Reconnect and try again.' : error instanceof Error ? error.message : 'Please try again.'}</div>
      </div>
      {retry && <button type="button" className="btn ghost sm" onClick={retry}>Try again</button>}
    </div>
  )
}
